import { Coordinate, PermissionStatus } from '@breakroom-nx/helpers';
import { firestore } from '@breakroom-nx/apis';
import { getDoc, doc } from 'firebase/firestore';
import { immerable, produce } from 'immer';

export class User {
  [immerable] = true;

  // Required for ALL users
  uid: string = '';
  avatar?: string;
  geoCode?: string;
  isAnonymous: boolean = false;
  points: number = 0;
  theme?: string;
  username: string = 'username';

  // Miscellaneous Member Variables
  isTeamMember: boolean = false;

  // Required Permissions for ALL Users
  cookiePermissions: PermissionStatus = PermissionStatus.NotSet;
  locationPermissions: PermissionStatus = PermissionStatus.NotSet;
  notificationPermissions: PermissionStatus = PermissionStatus.NotSet;

  /**
   * Optional Parameters
   */

  // Optional since Anonymous users don't go through onboarding
  completedOnboarding: boolean = false;

  // Online Status Object - Might be Disabled
  onlineStatus?: { lastOnline: Date; isOnline: boolean } = {
    lastOnline: new Date(),
    isOnline: false,
  };

  // Personal Details - Might be Disabled
  dateOfBirth?: Date;
  name?: string;
  bio?: string;

  // Location - Might be Disabled
  // can use general location from IP, no location permissions required
  location?: Coordinate;
  city?: string;
  country?: string;
  geohash?: string;

  // Anonymity Member Variables
  alias?: string;
  avatarAlias?: string;

  public constructor(init?: Partial<User>) {
    Object.assign(this, init);
    // Cache.setUser(this.uid, init as User);
  }

  getAge(): number | undefined {
    if (this.dateOfBirth) {
      const ageDifMs = Date.now() - this.dateOfBirth.getTime();
      const ageDate = new Date(ageDifMs);
      return Math.abs(ageDate.getUTCFullYear() - 1970);
    } else {
      return undefined;
    }
  }

  getLocation(): string {
    if (this.city && this.geoCode) {
      return `${this.city}, ${this.geoCode.toUpperCase()}`;
    } else {
      return '';
    }
  }

  getUsername(): string {
    if (this.isAnonymous || !this.name) {
      return this.username;
    } else {
      return this.name;
    }
  }
}

// Converts App User Object to Firestore Data Fields and Back
export const userConverter = {
  toFirestore: (user: User) => {
    return {
      username: user.username,
      onlineStatus: {
        lastOnline: user.onlineStatus?.lastOnline,
        isOnline: user.onlineStatus?.isOnline,
      },

      points: user.points ?? 0,
      isAnonymous: user.isAnonymous,
      completedOnboarding: user.completedOnboarding,
      cookiePermissions: user.cookiePermissions,
      locationPermissions: user.locationPermissions,
      notificationPermissions: user.notificationPermissions,

      // Optional Params
      dateOfBirth: user.dateOfBirth ?? null,
      name: !user.name || user.name === '' ? null : user.name,
      avatar: user.avatar ?? null,
      bio: user.bio ?? null,

      // Location
      location: user.location ?? null,
      country: user.country ?? null,
      city: user.city ?? null,

      geoCode: user.geoCode ?? null,
      geohash: user.geohash ?? null,

      // Anonymity Member Variables
      alias: user.alias ?? null,
      avatarAlias: user.avatarAlias ?? null,
      theme: user.theme ?? null,

      // Miscellaneous Member Variables
      isTeamMember: user.isTeamMember ?? null,
    };
  },
  fromFirestore: (snapshot: any) => {
    const data = snapshot.data();
    const uid = snapshot.id;

    return new User({
      uid: uid,
      username: data.username,
      onlineStatus: data.onlineStatus
        ? {
            isOnline: data.onlineStatus.isOnline ?? false,
            lastOnline: data.onlineStatus.lastOnline.toDate() ?? new Date(),
          }
        : undefined,
      points: data.points,
      isAnonymous: data.isAnonymous,
      completedOnboarding: data.completedOnboarding,
      cookiePermissions: data.cookiePermissions,
      locationPermissions: data.locationPermissions,
      notificationPermissions: data.notificationPermissions,

      // Optional Params
      dateOfBirth: data.dateOfBirth ? data.dateOfBirth.toDate() : null,
      name: data.name,
      avatar: data.avatar,
      bio: data.bio,

      // Location
      location: data.location ?? null,
      country: data.country ?? null,
      city: data.city ?? null,

      geoCode: data.geoCode,
      geohash: data.geohash,

      // Anonymity Member Variables
      alias: data.alias,
      avatarAlias: data.avatarAlias,
      theme: data.theme,

      // Miscellaneous Member Variables
      isTeamMember: data.isTeamMember ?? false,
    });
  },
};

// ! - Add Error Catch Blocks Get User Data from Firebase
export async function getUserData(uid: string): Promise<User | null> {
  const docRef = doc(firestore, 'users', uid).withConverter(userConverter);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    const user = docSnap.data();
    return user;
  } else {
    // console.log('No such document!');
    return null;
  }
}
